import { sb } from '../supabase';

// Data layer for the claim-invitation campaign (/admin2/claim-campaign).
//
// Every read goes through a SECURITY DEFINER RPC rather than a table select.
// That is not a style choice: the campaign status depends on wa_messages, which
// the browser cannot read at all (RLS enabled, no policies, grants revoked from
// authenticated). The RPC is also the ONLY definition of the five statuses —
// this screen renders them, it never recomputes them, because the sender
// applies the same rules and the two must not be able to disagree.

/** Render order, which is also the order the header counters appear in. */
export const CAMPAIGN_STATUSES = [
  'not_invited',
  'invited',
  'claimed',
  'opted_out',
  'no_valid_phone',
];

export const STATUS_LABEL = {
  not_invited:    { ar: 'لم تُدعَ',        en: 'Not invited',   tone: 'action' },
  invited:        { ar: 'دُعيت',           en: 'Invited',       tone: 'info' },
  claimed:        { ar: 'طالبت بصفحتها',   en: 'Claimed',       tone: 'ok' },
  opted_out:      { ar: 'انسحبت',          en: 'Opted out',     tone: 'muted' },
  no_valid_phone: { ar: 'بلا رقم صالح',    en: 'No valid phone', tone: 'bad' },
};

/**
 * Only the actionable pool can be selected. An `invited` row is deliberately
 * NOT re-selectable: event_key is UNIQUE, so a second send for the same factory
 * is rejected by the database and would only ever report 'duplicate'.
 */
export function isSelectable(row) {
  return row?.status === 'not_invited';
}

/**
 * The FOLLOW-UP campaign selects the opposite pool: a factory that received the
 * first invite and has not claimed. `invited` is exactly that status — claimed,
 * opted_out and no_valid_phone are all terminal for this purpose, and
 * not_invited has nothing to follow up.
 *
 * ⚠️ This is a WEAKER condition than the sender applies, and deliberately so.
 * admin_send_factory_claim additionally requires a claim_invite row with
 * status = 'sent' (not merely enqueued or failed) and at least one product.
 * Neither fact is on the board row, so a factory can look selectable here and
 * come back `not_invited` or `no_products` from the dry run.
 *
 * That is the right direction for the two to disagree: the screen offers, the
 * server decides, and the dry run is what the admin sees before anything is
 * sent. Making this stricter would mean shipping product counts and per-message
 * statuses to the browser for 100+ factories to answer a question the dry run
 * already answers exactly.
 */
export function isFollowUpSelectable(row) {
  return row?.status === 'invited';
}

/**
 * Effective consent for one campaign row — an opt-in that a later opt-out has
 * not superseded. Same rule as hasEffectiveConsent in the sender's core.ts, and
 * it must stay that way: this only decides what the screen shows, the sender
 * decides what actually goes out.
 */
export function hasConsent(row) {
  if (!row?.opt_in_at) return false;
  if (!row.opt_out_at) return true;
  return new Date(row.opt_out_at).getTime() < new Date(row.opt_in_at).getTime();
}

export async function fetchCampaignRows() {
  const { data, error } = await sb.rpc('admin_claim_campaign_rows');
  if (error) throw error;
  return data || [];
}

/**
 * Consent documentation over every active factory with a valid WhatsApp number.
 *
 * Always call it with dryRun first and show the counts: `p_dry_run` returns the
 * outcome each factory WOULD get, from the same code path that performs the
 * write, so the confirmation dialog can never quote a number the execution then
 * contradicts. That is the whole reason the parameter exists — the client must
 * not re-derive "who gets marked".
 *
 * The real call is the moment consent stops being zero. It is the owner's
 * action, taken from this screen, and nothing else in the app invokes it.
 */
export async function markPriorContactBulk({ dryRun = false } = {}) {
  const { data, error } = await sb.rpc('admin_mark_prior_contact_bulk', { p_dry_run: dryRun });
  if (error) throw error;
  return data || [];
}

/** Count rows by their `outcome`, preserving a stable display order. */
export function summarizeOutcomes(rows) {
  const out = {};
  (rows || []).forEach((r) => { out[r.outcome] = (out[r.outcome] || 0) + 1; });
  return out;
}

// `marked` carries both tenses: the same dialog shows the preview and then the
// result, and "will be marked" left standing after the write would be a claim
// about the future made about something already done.
export const OUTCOME_LABEL = {
  marked:            { ar: 'مؤهَّل للوسم',       en: 'Will be marked',
                       arDone: 'وُسِم',           enDone: 'Marked' },
  skipped_opted_out: { ar: 'متخطّى — انسحب',     en: 'Skipped — opted out' },
  skipped_stronger:  { ar: 'متخطّى — مصدر أقوى', en: 'Skipped — stronger source' },
  skipped_already:   { ar: 'متخطّى — موسوم سلفًا', en: 'Skipped — already recorded' },
};

/**
 * Daily volume for the campaign number. A SOFT limit: the UI warns past it and
 * still lets the send through, because the number that matters is the one the
 * person sending is watching, not one the code guesses. The database applies no
 * cap at all, on purpose.
 */
export const DAILY_SOFT_CAP = 20;

/**
 * MIRROR of the claim_invite template as approved at Meta.
 *
 * Nothing here is authoritative. The template lives in the Widers console and
 * META renders it — we send only the variables. This copy exists so the confirm
 * dialog can show what a factory actually receives before 107 of them get it.
 *
 * ⚠️ HAND-MAINTAINED. It MUST be updated whenever the template changes, and
 * nothing in the system will tell you when that happens. A stale mirror shows
 * an admin a message that is not the one being sent, which is worse than
 * showing no preview at all.
 *
 * Approved shape: ONE URL button, en_US only. (docs/wa-template-claim-invite.md
 * still describes two buttons, a zh_CN body and a different draft body — it is
 * stale throughout and is corrected separately.)
 */
export const CLAIM_INVITE_TEMPLATE = {
  // The language actually sent, traced rather than guessed:
  // admin_send_claim_invites passes 'en' (20260908000003), and META_LOCALE in
  // whatsapp-send/providers/widers.ts maps en -> en_US. Meta approves per
  // locale, so en_US is what the recipient sees.
  lang: 'en_US',

  // Copied from the APPROVED template on 2026-09-08 — not from
  // docs/wa-template-claim-invite.md, whose body is a superseded draft.
  // {{1}} is the only body variable; the sender fills it with
  // company_name_latin, falling back to company_name.
  //
  // The two ✅ lines are ONE paragraph joined by a single newline. The preview
  // renders with white-space: pre-line for exactly that reason.
  body: [
    'Hello {{1}} 👋',
    'Following our previous contact, your factory page on MAABAR is ready 🔖',
    '🇸🇦 Claim your page to browse and receive requests directly from Saudi traders.',
    '✅ Free registration\n✅ No commission for suppliers',
    '⏱️ Takes less than a minute.',
  ].join('\n\n'),

  footer: 'MAABAR — Saudi B2B sourcing platform',

  buttonLabel: 'Claim your page',

  // Configured IN THE TEMPLATE at Meta, not sent by us: the button carries this
  // base and receives claim_slug as its dynamic suffix. Mirrored here only so
  // the preview can show the resolved link.
  urlBase: 'https://maabar.io/claim/',
};

/**
 * Per-message price used for the dialog's estimate. HAND-MAINTAINED: Meta
 * prices per recipient country, and this is the China marketing rate, which is
 * where the overwhelming majority of this directory sits. Always shown as an
 * estimate, never as a charge.
 */
export const MARKETING_RATE_SAR = 0.30;

/**
 * Queue a claim invite per factory. Like the consent action, always preview
 * with dryRun first: the board's status can be minutes old, and a factory may
 * have claimed or opted out since it loaded. The dry run recomputes eligibility
 * server-side at send time.
 */
export async function sendClaimInvites(ids, { dryRun = false } = {}) {
  const { data, error } = await sb.rpc('admin_send_claim_invites', {
    p_factory_ids: ids,
    p_dry_run: dryRun,
  });
  if (error) throw error;
  return data || [];
}

/**
 * Queue the FOLLOW-UP (factory_claim) per factory. Same contract as
 * sendClaimInvites — always preview with dryRun first, because the board's
 * status can be minutes old and the server recomputes eligibility at send time.
 *
 * Returns one row per factory with an extra `product_count` column the first
 * campaign does not have: it is {{2}} of the template, so the preview can show
 * the number each factory will actually be told.
 */
export async function sendFactoryClaim(ids, { dryRun = false } = {}) {
  const { data, error } = await sb.rpc('admin_send_factory_claim', {
    p_factory_ids: ids,
    p_dry_run: dryRun,
  });
  if (error) throw error;
  return data || [];
}

/**
 * MIRROR of the factory_claim template — the follow-up's equivalent of
 * CLAIM_INVITE_TEMPLATE, and carrying the same warning: nothing in the system
 * will tell you when it goes stale.
 *
 * READ FROM THE WIDERS SEND SCREEN on 2026-09-16, NOT from the template
 * document. That distinction is the point. The document is the weaker source:
 * it omits claim_invite entirely, its request_received entry disagreed with
 * what production was sending, and its version of this body had no line break
 * after the greeting. The console confirmed two body variables and one button
 * field, which is what closed the shape question against the source rather than
 * against a copy of it.
 *
 * ⚠️ HAND-MAINTAINED, and the reason this is still a copy at all: reading the
 * body from Widers at preview time is the better design and is deferred, not
 * rejected — see docs/TECH_DEBT.md. Until then, a template edited at Meta and
 * not edited here shows an admin a message that is not the one being sent.
 *
 * Reproduced line for line as the console displays it. Single newlines, no
 * blank lines: the preview renders with white-space: pre-line so both single
 * and double breaks survive. Exact spacing is the one thing worth re-checking
 * against a received message rather than a screen.
 */
export const FACTORY_CLAIM_TEMPLATE = {
  // The sender passes 'en' and META_LOCALE maps en -> en_US. factory_claim is
  // also approved in zh_CN, which is not used yet — see the language note in
  // migration 20260916031942.
  lang: 'en_US',

  body: [
    'Hello {{1}},',
    'this is Maabar, the Saudi sourcing platform. We are a platform, not a buyer.',
    'Your factory page is ready on Maabar with {{2}} products already listed. Claim it to manage your page and receive purchase orders from Saudi traders directly.',
  ].join('\n'),

  // No footer on this one. claim_invite carries "MAABAR — Saudi B2B sourcing
  // platform"; the console showed none here, and an invented footer would be
  // the same error as invented copy. The preview omits it when null.
  footer: null,

  buttonLabel: 'Claim your page',

  // ⚠️ The console shows the button as https://maabar.io/claim/{{1}} — and that
  // {{1}} is the BUTTON'S OWN variable, not the body's. WhatsApp numbers a URL
  // button's parameters independently of the body's, so button {{1}} is the
  // claim slug (the console's own example was "BE2QRQP"), NOT the factory name
  // that fills body {{1}}.
  //
  // Do not "fix" the sender to pass factory_name here. It would build a URL
  // like /claim/Lian%20Wei%20Clothing, which resolves to nothing, and the
  // message would still be delivered — a dead button on a live campaign.
  urlBase: 'https://maabar.io/claim/',
};

/**
 * Follow-up outcomes. Shares most of its vocabulary with the first campaign and
 * adds the two gates only this one has.
 *
 * The dialog renders Object.keys of the map it is given, so an outcome missing
 * here is counted in the skipped total and then shown on NO line at all — the
 * same trap documented on SEND_OUTCOME_LABEL. Both new outcomes are here.
 */
export const FOLLOW_UP_OUTCOME_LABEL = {
  enqueued:    { ar: 'سيُرسَل',              en: 'Will send',
                 arDone: 'أُدرج في الطابور',  enDone: 'Queued' },
  duplicate:   { ar: 'متخطّى — تابعناه سلفًا', en: 'Skipped — already followed up' },
  claimed:     { ar: 'متخطّى — طالب بصفحته',  en: 'Skipped — already claimed' },
  opted_out:   { ar: 'متخطّى — انسحب',        en: 'Skipped — opted out' },
  no_consent:  { ar: 'متخطّى — بلا موافقة موثّقة', en: 'Skipped — consent not documented' },
  no_phone:    { ar: 'متخطّى — بلا رقم صالح',  en: 'Skipped — no valid number' },
  inactive:    { ar: 'متخطّى — صفّ مخفي',      en: 'Skipped — hidden row' },
  // Only this campaign has these two.
  // not_invited means the first invite never SENT — it may have been queued or
  // failed, which the board still shows as "invited". Following up on a message
  // that never arrived reads wrong, so the server refuses.
  not_invited: { ar: 'متخطّى — لم تصله الدعوة الأولى', en: 'Skipped — first invite never sent' },
  no_products: { ar: 'متخطّى — بلا منتجات',    en: 'Skipped — no products' },
  not_found:   { ar: 'غير موجود',              en: 'Not found' },
};

export const SEND_OUTCOME_LABEL = {
  enqueued:   { ar: 'سيُرسَل',              en: 'Will send',
                arDone: 'أُدرج في الطابور',  enDone: 'Queued' },
  duplicate:  { ar: 'متخطّى — دُعي سلفًا',   en: 'Skipped — already invited' },
  claimed:    { ar: 'متخطّى — طالب بصفحته', en: 'Skipped — already claimed' },
  opted_out:  { ar: 'متخطّى — انسحب',        en: 'Skipped — opted out' },
  // Distinct from opted_out on purpose: this one is one click from fixed, by
  // the "Document consent" button at the top of the screen. The dialog renders
  // Object.keys of this map, so an outcome absent here is counted in the
  // skipped total and then shown on no line at all.
  no_consent: { ar: 'متخطّى — بلا موافقة موثّقة', en: 'Skipped — consent not documented' },
  no_phone:   { ar: 'متخطّى — بلا رقم صالح', en: 'Skipped — no valid number' },
  inactive:   { ar: 'متخطّى — صفّ مخفي',     en: 'Skipped — hidden row' },
  not_found:  { ar: 'غير موجود',             en: 'Not found' },
};

/**
 * The label for one outcome, in the tense the dialog phase calls for.
 * `table` selects which vocabulary — consent documentation or sending.
 */
export function outcomeLabel(key, isAr, done = false, table = OUTCOME_LABEL) {
  const m = table[key];
  if (!m) return key;
  if (done) return (isAr ? m.arDone : m.enDone) || (isAr ? m.ar : m.en);
  return isAr ? m.ar : m.en;
}
